import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/lib/use-auth';

/**
 * Pantalla de cambio de contraseña.
 *
 * Visible bajo dos circunstancias:
 *   1. Acceso voluntario desde "Ajustes".
 *   2. Forzado tras el primer login (passwordChangeRequired).
 *
 * En el caso 2, RequireAuth bloquea cualquier otra ruta hasta que la
 * contraseña se cambie.
 */
export function ChangePassword() {
  const { user, changePassword } = useAuth();
  const navigate = useNavigate();
  const forced = !!user?.passwordChangeRequired;

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (newPassword !== confirm) {
      setError('La nueva contraseña y su confirmación no coinciden.');
      return;
    }
    if (newPassword.length < 10) {
      setError('La nueva contraseña debe tener al menos 10 caracteres.');
      return;
    }
    setSubmitting(true);
    try {
      await changePassword(currentPassword, newPassword);
      navigate(forced ? '/' : '/ajustes', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('No se ha podido conectar con el servidor.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-md mx-auto p-6 md:p-8">
      <header className="mb-6 text-center">
        <h1 className="text-2xl font-semibold tracking-tight2">
          {forced ? 'Cambia tu contraseña' : 'Cambiar contraseña'}
        </h1>
        <p className="text-[14px] text-muted-foreground mt-1">
          {forced
            ? 'Es la primera vez que accedes (o un administrador ha reseteado tu cuenta). Define una contraseña nueva para continuar.'
            : 'Necesitarás conocer la actual.'}
        </p>
      </header>

      <form
        onSubmit={onSubmit}
        className="bg-card border border-border rounded-sm p-6 shadow-sm flex flex-col gap-4"
      >
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="current">{forced ? 'Contraseña temporal' : 'Contraseña actual'}</Label>
          <Input
            id="current"
            type="password"
            autoComplete="current-password"
            required
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="new">Nueva contraseña</Label>
          <Input
            id="new"
            type="password"
            autoComplete="new-password"
            required
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <p className="text-[12px] text-muted-foreground">Mínimo 10 caracteres.</p>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="confirm">Confirmar nueva contraseña</Label>
          <Input
            id="confirm"
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </div>

        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}

        <div className="flex gap-2 pt-1">
          {!forced ? (
            <Button asChild type="button" variant="ghost" className="flex-1">
              <Link to="/ajustes">Cancelar</Link>
            </Button>
          ) : null}
          <Button type="submit" variant="primary" className="flex-1" disabled={submitting}>
            {submitting ? 'Guardando…' : 'Guardar nueva contraseña'}
          </Button>
        </div>
      </form>
    </div>
  );
}
